import type { TrialBalanceAsOn, TrialBalanceAsOnRow } from "@/api/types";
import type { StatementRow } from "./reportParts";

const paisa = (value: string) => Math.round(Number(value) * 100);
const rupees = (value: number) => (value / 100).toFixed(2);

/** Debit-natural for assets, credit-natural for what funds them. */
const held = (row: TrialBalanceAsOnRow) =>
  row.group === "Assets"
    ? paisa(row.debit) - paisa(row.credit)
    : paisa(row.credit) - paisa(row.debit);

/** What the year has earned so far: not yet posted to equity, and part of what balances the sheet. */
const resultOf = (rows: TrialBalanceAsOnRow[]) =>
  rows
    .filter((row) => row.group === "Income" || row.group === "Expenses")
    .reduce((sum, row) => sum + paisa(row.credit) - paisa(row.debit), 0);

export interface BalanceSheet {
  rows: StatementRow[];
  /** Assets less liabilities and equity. Anything but zero is a bug worth showing. */
  difference: number;
}

export function balanceSheetOf(current: TrialBalanceAsOn, prior?: TrialBalanceAsOn): BalanceSheet {
  const now = current.rows;
  const then = prior?.rows ?? [];
  const columns = (paisaNow: number, paisaThen: number) =>
    prior ? [rupees(paisaNow), rupees(paisaThen)] : [rupees(paisaNow)];
  const nothing = () => (prior ? [null, null] : [null]);

  const section = (group: "Assets" | "Liabilities" | "Equity") => {
    const codes = [
      ...new Set([...now, ...then].filter((r) => r.group === group).map((r) => r.code)),
    ].sort();
    const totalNow = now.filter((r) => r.group === group).reduce((s, r) => s + held(r), 0);
    const totalThen = then.filter((r) => r.group === group).reduce((s, r) => s + held(r), 0);
    const lines: StatementRow[] = codes.map((code) => {
      const a = now.find((r) => r.code === code);
      const b = then.find((r) => r.code === code);
      return {
        label: (a ?? b)!.name,
        kind: "line",
        values: columns(a ? held(a) : 0, b ? held(b) : 0),
      };
    });
    return { lines, totalNow, totalThen };
  };

  const assets = section("Assets");
  const liabilities = section("Liabilities");
  const equity = section("Equity");
  const resultNow = resultOf(now);
  const resultThen = resultOf(then);
  const equityNow = equity.totalNow + resultNow;
  const equityThen = equity.totalThen + resultThen;

  return {
    rows: [
      { label: "Assets", kind: "header", values: nothing() },
      ...assets.lines,
      { label: "Total assets", kind: "total", values: columns(assets.totalNow, assets.totalThen) },
      { label: "Liabilities", kind: "header", values: nothing() },
      ...liabilities.lines,
      {
        label: "Total liabilities",
        kind: "subtotal",
        values: columns(liabilities.totalNow, liabilities.totalThen),
      },
      { label: "Equity", kind: "header", values: nothing() },
      ...equity.lines,
      {
        label: "Result for the year to date",
        kind: "line",
        values: columns(resultNow, resultThen),
      },
      { label: "Total equity", kind: "subtotal", values: columns(equityNow, equityThen) },
      {
        label: "Total liabilities and equity",
        kind: "total",
        values: columns(liabilities.totalNow + equityNow, liabilities.totalThen + equityThen),
      },
    ],
    difference: assets.totalNow - liabilities.totalNow - equityNow,
  };
}
